import React, { useEffect } from 'react';
import cn from 'classnames';

import {
  useAppDispatch,
  useAppSelector,
  useTranslation,
  useResponsive,
  useGAEventCommonProps,
  useMuxSpace,
} from 'client/hooks';
import { analytics } from 'client/utils/analytics';

import { ROOM_STATUS, USER_ROLES } from 'constants/index';
import { OUTPUT_LINK } from 'client/constants';

import { setIsShowJoinToSpacePopup, setShouldInitPubnub } from 'client/redux/ui';
import { selectIsBusy, selectRoomId } from 'client/redux/room/selectors';
import { selectIsBroadcasting } from 'client/redux/mux-details/selectors';
import { selectRoomDbData, selectRoomStatus } from 'client/redux/room/selectors';
import { selectIsLoggedIn, selectUserRoomRole } from 'client/redux/client/selectors';
import { selectShouldInitPubnub } from 'client/redux/ui/selectors';
import wakeLock from 'client/utils/wake-lock';
import { useLiveStreamContext } from 'client/components/LiveStreamConnector/context';

import Icon from '../../Icon';
import Timer from '../../Timer';
import Button from '../../Button';
import WaitersList from '../WaitersList';
import { TermsFooter } from '../../TermsFooter';
import WaiterSpeakContainer from '../WaiterSpeakContainer';

import css from './RoomBottomContent.module.scss';

interface Props {
  className?: string;
}

const RoomBottomContent: React.FC<Props> = ({ className = '' }) => {
  const { join } = useMuxSpace();
  const { state } = useLiveStreamContext();
  const dispatch = useAppDispatch();
  const roomStatus = useAppSelector(selectRoomStatus);
  const shouldInitPubnub = useAppSelector(selectShouldInitPubnub);
  const isBroadcasting = useAppSelector(selectIsBroadcasting);
  const isRoomFetching = useAppSelector(selectIsBusy);
  const { translate } = useTranslation('room');
  const { translate: t } = useTranslation('header');
  const roomData = useAppSelector(selectRoomDbData);
  const roomId = useAppSelector(selectRoomId);
  const userRole = useAppSelector((state) => selectUserRoomRole(state, roomId));
  const [isMatchingMQ] = useResponsive(['MOBILE']);
  const { common } = useGAEventCommonProps();
  const needToOpenJoinPopup = React.useRef(false);

  const [isCountdownEnded, setIsCountdownEnded] = React.useState(false);
  const isLoggedIn = useAppSelector(selectIsLoggedIn);

  const isRoomInactive = roomStatus === ROOM_STATUS.INACTIVE;
  const isRoomActive = roomStatus === ROOM_STATUS.ACTIVE;
  const isRoomFinished = roomStatus === ROOM_STATUS.FINISHED;
  const isCanceled = roomStatus === ROOM_STATUS.CANCELLED;

  const onExitBtnClick = () => {
    analytics.gtag.event('audio_chat_content_click', {
      ...common,
      text_clicked: t('logoutLabel'),
    });
    window.location.href = OUTPUT_LINK;
  };

  React.useEffect(() => {
    if (shouldInitPubnub && !isBroadcasting) {
      needToOpenJoinPopup.current = true;
    }

    if (state.space && shouldInitPubnub && !isLoggedIn && !state.localParticipant) {
      if (needToOpenJoinPopup.current) dispatch(setIsShowJoinToSpacePopup(true));
      else joinProcess();
    }

    async function joinProcess() {
      wakeLock.request();
      await join({ canPublish: false, micInitialState: 'muted' });

      analytics.gtag.event('audio_chat_content_click', {
        ...common,
        text_clicked: translate('listenerActivateMicrophonePopUp.muteBtn'),
      });
    }
  }, [state.space, shouldInitPubnub, state.localParticipant, isLoggedIn, isBroadcasting]);

  const onVisitAnonymousBtnClick = async () => {
    dispatch(setShouldInitPubnub(true));
  };

  const renderTextsAndButtons = () => {
    if (isMatchingMQ === undefined) return null;

    if (!isLoggedIn && !isRoomFinished) {
      return (
        <>
          <div className={css.subtitle}>
            <span>{translate('subtitle')}</span>
          </div>
          <div className={css.loginButtons}>
            {!shouldInitPubnub && (
              <Button
                className={css.regular}
                onClickHandler={onVisitAnonymousBtnClick}
                label={translate('loginButtons.enterAsAnonymous')}
                disabled={isRoomFetching}
              />
            )}
          </div>
        </>
      );
    }
    if (isRoomFinished && userRole === USER_ROLES.LISTENER) {
      return (
        <>
          <div className={css.subtitle}>
            <span>{translate('subtitleFinished')}</span>
          </div>
          <div className={css.loginButtons}>
            <Button label={t('logoutLabel')} className={css.regular} onClickHandler={onExitBtnClick} />
          </div>
        </>
      );
    }
    return null;
  };

  useEffect(() => {
    if (roomData?.date && isRoomActive) {
      const dateOfStart = new Date(roomData?.date);
      if (dateOfStart.valueOf() < Date.now()) {
        setIsCountdownEnded(true);
      }
    }
  }, [isRoomActive, roomData?.date]);

  if (isRoomInactive || isCanceled) {
    return (
      <div className={cn(css.roomData, className)}>
        <div className={css.inactive} data-testid="room-bottom-content-inactive">
          <Icon type="lock" />
          <span>{translate('lockScreen.label')}</span>
        </div>

        <TermsFooter className={css.footer} />
      </div>
    );
  }

  return (
    <>
      {!isBroadcasting && (isRoomActive || isRoomFinished) ? (
        <div className={cn(css.roomData, className)} data-testid="not-on-air-room-bottom-content">
          <div className={css.title}>
            {isRoomActive && (
              <>
                {isCountdownEnded && shouldInitPubnub && <span>{translate('title')}</span>}
                {!isCountdownEnded && shouldInitPubnub && (
                  <>
                    <span>{translate('timerTitle')} </span> <br />
                    <span>
                      {roomData?.date && isMatchingMQ !== undefined && (
                        <Timer date={roomData.date} handleCountdownEnded={() => setIsCountdownEnded(true)} />
                      )}
                    </span>
                  </>
                )}
              </>
            )}
            {isRoomFinished && <span>{translate('titleFinished')}</span>}
          </div>
          {renderTextsAndButtons()}

          <TermsFooter className={css.footer} />
        </div>
      ) : (
        <div className={css.waitersContainer} data-testid="on-air-room-bottom-content">
          <div className={css.waiterSpeak}>
            <WaiterSpeakContainer />
          </div>
          <div className={css.list}>
            <WaitersList />
          </div>

          <TermsFooter className={css.footer} />
        </div>
      )}
    </>
  );
};

export default RoomBottomContent;
